import { useState, useMemo } from "react";
import { useInvoices } from "../../contexts/InvoicesContext";
import { useExpenses } from "../../contexts/ExpensesContext";

const NAVY = '#0C2D48';
const BORDER = '#e0e0e0';

function fmtMoney(n) {
  return `£${Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtDate(str) {
  if (!str) return '—';
  return new Date(str).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

const TYPE_COLORS = {
  income:  { bg: '#eaf6ef', text: '#1e6645', border: '#6abf85', label: 'Income' },
  expense: { bg: '#fde8e8', text: '#A12710', border: '#f5a5a5', label: 'Expense' },
};

const RANGE_OPTIONS = [
  { key: 'all',         label: 'All time' },
  { key: 'this-month',  label: 'This month' },
  { key: 'last-month',  label: 'Last month' },
  { key: 'this-year',   label: 'This year' },
];

function inRange(dateStr, key) {
  if (key === 'all' || !dateStr) return true;
  const d = new Date(dateStr);
  const now = new Date();
  if (key === 'this-month') return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  if (key === 'last-month') {
    const lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    return d.getFullYear() === lm.getFullYear() && d.getMonth() === lm.getMonth();
  }
  if (key === 'this-year') return d.getFullYear() === now.getFullYear();
  return true;
}

export default function TransactionsList() {
  const [range, setRange] = useState('this-month');
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  const { invoices } = useInvoices();
  const { expenses } = useExpenses();

  const raw = useMemo(() => {
    const rows = [];
    for (const inv of invoices) {
      if (!inv.lineItems) continue;
      const total = inv.lineItems.reduce((s, r) => s + r.qty * r.unitPrice * (1 + r.tax / 100), 0);
      rows.push({
        id: `inv_${inv.id}`,
        date: inv.paidAt || inv.issueDate,
        description: `Invoice ${inv.number || inv.id} — ${inv.contactName || 'Client'}`,
        type: 'income',
        category: 'Invoice',
        amount: total,
        status: inv.status,
        reference: inv.number || '',
      });
    }
    for (const exp of expenses) {
      rows.push({
        id: `exp_${exp.id}`,
        date: exp.date,
        description: exp.title || 'Expense',
        type: 'expense',
        category: exp.category || 'General',
        amount: -(exp.amount || 0),
        status: exp.receipt_url ? 'receipted' : 'logged',
        reference: '',
      });
    }
    rows.sort((a, b) => new Date(b.date) - new Date(a.date));
    return rows;
  }, [invoices, expenses]);

  const rows = useMemo(() => raw.filter(r => {
    if (!inRange(r.date, range)) return false;
    if (typeFilter !== 'all' && r.type !== typeFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return r.description.toLowerCase().includes(q) || r.category.toLowerCase().includes(q) || r.reference.toLowerCase().includes(q);
    }
    return true;
  }), [raw, range, typeFilter, search]);

  const totalIncome  = rows.filter(r => r.type === 'income').reduce((s, r) => s + r.amount, 0);
  const totalExpense = rows.filter(r => r.type === 'expense').reduce((s, r) => s + Math.abs(r.amount), 0);
  const net          = totalIncome - totalExpense;

  function exportCSV() {
    const header = 'Date,Description,Type,Category,Amount,Reference\n';
    const body = rows.map(r =>
      `"${fmtDate(r.date)}","${r.description}","${r.type}","${r.category}","${r.amount.toFixed(2)}","${r.reference}"`
    ).join('\n');
    const blob = new Blob([header + body], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transactions_${range}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div style={{ fontFamily: "'Raleway', sans-serif", maxWidth: 900 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <div>
          <div style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1.15rem', color: NAVY }}>Transactions</div>
          <div style={{ fontSize: '0.8rem', color: '#aaa', marginTop: 2 }}>
            {rows.length} transaction{rows.length !== 1 ? 's' : ''}
          </div>
        </div>
        <button
          onClick={exportCSV}
          style={{ padding: '0.45rem 1rem', background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', color: '#555', fontFamily: "'Raleway', sans-serif" }}
        >
          ⬇ Export CSV
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.25rem' }}>
        {[
          { label: 'Total Income',  value: fmtMoney(totalIncome),  color: '#1e6645' },
          { label: 'Total Expenses',value: fmtMoney(totalExpense), color: '#A12710' },
          { label: 'Net',           value: (net >= 0 ? '+' : '−') + fmtMoney(net), color: net >= 0 ? '#1e6645' : '#A12710' },
        ].map(({ label, value, color }) => (
          <div key={label} style={{ flex: 1, minWidth: 140, background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, padding: '0.85rem 1rem' }}>
            <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>{label}</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 900, color }}>{value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search transactions…"
          style={{ flex: '1 1 180px', border: `1px solid ${BORDER}`, borderRadius: 8, padding: '0.4rem 0.75rem', fontSize: '0.83rem', fontFamily: "'Raleway', sans-serif", outline: 'none' }}
        />
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          style={{ border: `1px solid ${BORDER}`, borderRadius: 8, padding: '0.4rem 0.65rem', fontSize: '0.83rem', fontFamily: "'Raleway', sans-serif", outline: 'none', background: '#fff', color: '#555' }}
        >
          <option value="all">All types</option>
          <option value="income">Income</option>
          <option value="expense">Expense</option>
        </select>
        <div style={{ display: 'flex', gap: 4 }}>
          {RANGE_OPTIONS.map(opt => (
            <button
              key={opt.key}
              onClick={() => setRange(opt.key)}
              style={{
                padding: '0.38rem 0.75rem', borderRadius: 20,
                border: `1px solid ${range === opt.key ? NAVY : BORDER}`,
                background: range === opt.key ? NAVY : '#fff',
                color: range === opt.key ? '#fff' : '#555',
                fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
                fontFamily: "'Raleway', sans-serif",
                transition: 'all 0.12s',
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', border: `1px solid ${BORDER}`, borderRadius: 10, overflow: 'hidden' }}>
        {rows.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#bbb', fontSize: '0.9rem' }}>
            No transactions found for this period.
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem' }}>
              Paid invoices and logged expenses will appear here.
            </div>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: `2px solid ${BORDER}` }}>
                {['Date', 'Description', 'Category', 'Type', 'Amount'].map(h => (
                  <th key={h} style={{ padding: '0.6rem 0.9rem', textAlign: h === 'Amount' ? 'right' : 'left', fontWeight: 700, color: '#555', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                const tc = TYPE_COLORS[r.type] || TYPE_COLORS.income;
                return (
                  <tr key={r.id} style={{ borderBottom: i < rows.length - 1 ? `1px solid ${BORDER}` : 'none', background: i % 2 === 1 ? '#fafafa' : '#fff' }}>
                    <td style={{ padding: '0.6rem 0.9rem', color: '#666', whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                    <td style={{ padding: '0.6rem 0.9rem', color: '#333', fontWeight: 500 }}>
                      {r.description}
                      {r.reference && <span style={{ color: '#bbb', fontSize: '0.75rem', marginLeft: 6 }}>#{r.reference}</span>}
                    </td>
                    <td style={{ padding: '0.6rem 0.9rem', color: '#888' }}>{r.category}</td>
                    <td style={{ padding: '0.6rem 0.9rem' }}>
                      <span style={{ background: tc.bg, color: tc.text, border: `1px solid ${tc.border}`, borderRadius: 10, padding: '2px 8px', fontSize: '0.73rem', fontWeight: 700 }}>
                        {tc.label}
                      </span>
                    </td>
                    <td style={{ padding: '0.6rem 0.9rem', textAlign: 'right', fontWeight: 700, color: r.type === 'income' ? '#1e6645' : '#A12710', fontFamily: "'Poppins', sans-serif" }}>
                      {r.type === 'income' ? '+' : '−'}{fmtMoney(r.amount)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
